/**
 * use-deep-live-poll — bridge `agent_run.live` snapshots into the deep store.
 *
 * The original tab that started a Deep Explorer run owns the WebSocket and
 * receives every `deep_activity_event` frame in real time. A second tab
 * opening the same session has no WS to subscribe to — the frames are
 * 1:1, not broadcast. Without this hook, the second tab sits on
 * AgentRunningBanner with an empty workspace until the run finishes and
 * messages hydrate.
 *
 * The Deep Explorer streaming loop now writes a periodic snapshot
 * (`agent_run.live`) to Cosmos every ~5s while a run is active (see
 * `_flush_live_snapshot` in `explorer_deep.py`). This hook polls
 * `GET /api/explorer-deep/state` while the current session has an active
 * agent run, dedupes events by id against whatever's already in the
 * store (so the originating tab's WS frames don't get duplicated), and
 * stops once the run reaches a terminal status.
 */
import { useEffect, useRef, useState } from "react"

import type { Artifact } from "@/api/models"

import { getDeepStateApi } from "@/api/api"
import {
  adoptChatRequestGeneration,
  getChatRequestGenerationId,
  getChatRequestSessionDisposalGeneration,
  isChatRequestSessionDisposalGenerationCurrent,
  isChatRequestSessionDisposed,
} from "@/features/chat/hooks/chat-request-registry"
import { logger } from "@/lib/logger"
import { SettingsService } from "@/services/settings-service"
import { getDeepStateForSession, useAIExplorerStore } from "@/stores/ai-explorer-store"
import { type DeepActivityEvent } from "@/stores/slices/deep-explorer-session-state"

import { isActiveAgentRunStatus, useAgentRunsQuery } from "./use-agent-runs-query"
import { deriveSourcesFromActivity } from "../utils/deriveSourcesFromActivity"

const POLL_INTERVAL_MS = 2500

interface UseDeepLivePollOptions {
  sessionType: string
  sessionId: string | null | undefined
  skipPolling?: boolean
}

export function useDeepLivePoll({
  sessionType,
  sessionId,
  skipPolling = false,
}: UseDeepLivePollOptions): void {
  const explorerStore = useAIExplorerStore(sessionType)
  const replaceDeepState = explorerStore((state) => state.replaceDeepState)
  const hydrateArtifactsFromContext = explorerStore((state) => state.hydrateArtifactsFromContext)
  const [settingsService] = useState<SettingsService>(() => SettingsService.getInstance())
  const agentRunsQuery = useAgentRunsQuery()

  const currentRun = sessionId
    ? (agentRunsQuery.data?.find((r) => r.session_id === sessionId) ?? null)
    : null
  const isActive = currentRun !== null && isActiveAgentRunStatus(currentRun.status)

  // Latest values referenced by the timer callback so we don't re-create
  // the interval on every store update.
  const explorerStoreRef = useRef(explorerStore)
  explorerStoreRef.current = explorerStore
  const replaceDeepStateRef = useRef(replaceDeepState)
  replaceDeepStateRef.current = replaceDeepState
  const hydrateArtifactsRef = useRef(hydrateArtifactsFromContext)
  hydrateArtifactsRef.current = hydrateArtifactsFromContext
  // Hold the *service*, not a render-time copy of its token (#3072). The auth
  // refresh coordinator republishes a refreshed bearer into SettingsService
  // without re-rendering this hook, so a token snapshot taken at render time
  // kept being sent long after it expired — every 2.5s poll then cost a 401
  // plus a replay, which is the "Token *** expired" log storm on
  // /api/explorer-deep/state. Reading at call time picks up the fresh one.
  const settingsServiceRef = useRef(settingsService)
  settingsServiceRef.current = settingsService

  useEffect(() => {
    if (!isActive || !sessionId || skipPolling) return

    let cancelled = false

    const poll = async () => {
      try {
        const store = explorerStoreRef.current
        const disposalGeneration = getChatRequestSessionDisposalGeneration(store, sessionId)
        const expectedRequestGenerationId = getChatRequestGenerationId(store, sessionId)
        const res = await getDeepStateApi(sessionId, settingsServiceRef.current.token)
        const responseGenerationId = res.request_generation_id
        if (
          cancelled ||
          !isChatRequestSessionDisposalGenerationCurrent(store, sessionId, disposalGeneration) ||
          isChatRequestSessionDisposed(store, sessionId) ||
          !responseGenerationId ||
          !adoptChatRequestGeneration(store, sessionId, {
            requestGenerationId: responseGenerationId,
            expectedRequestGenerationId,
          }) ||
          (res.live_request_generation_id
            ? res.live_request_generation_id !== responseGenerationId
            : Boolean(res.agent_run_request_generation_id))
        )
          return

        const incomingEvents = (res.live_activity_events ?? []) as unknown as DeepActivityEvent[]
        const incomingArtifacts = (res.live_artifacts ?? []) as unknown as Artifact[]

        // Activity events: dedupe against the existing log so the
        // originating tab's WS frames don't double-up here.
        //
        // Dedupe key is ``${id}:${event}`` — NOT just ``id`` — because the
        // backend reuses the same id for the start/end pair of a single
        // tool call (streaming.py:2846, "Reuse the event_id from
        // tool_start so the frontend can match them"). Keying on id alone
        // made a missed ``tool_end`` unrecoverable: the matching
        // ``tool_start`` was already in the log so the dedupe saw the id
        // as known and dropped the ``tool_end``. Result: rows stayed
        // in_progress forever after a WS drop. (2026-05-12 incident.)
        if (incomingEvents.length > 0) {
          const store = explorerStoreRef.current
          const prior = getDeepStateForSession(store.getState(), sessionId).activityLog
          const seen = new Set(prior.map((e) => `${e.id}:${e.event}`))
          const additions = incomingEvents.filter((e) => e.id && !seen.has(`${e.id}:${e.event}`))
          if (additions.length > 0) {
            const merged = [...prior, ...additions]
            replaceDeepStateRef.current(sessionId, {
              activityLog: merged,
              sources: deriveSourcesFromActivity(merged),
            })
          }
        }

        // Workspace artifacts — the existing hydrator dedupes by id, so
        // we can hand it the full live list each tick. The originating
        // tab's WS-driven artifact_emit handler still wins on later
        // updates because its frames overwrite by id too.
        if (incomingArtifacts.length > 0) {
          hydrateArtifactsRef.current(sessionId, incomingArtifacts)
        }
      } catch (err) {
        logger.warn("useDeepLivePoll: live snapshot fetch failed", err as Error)
      }
    }

    // Fire one immediately so the panel populates without waiting a full
    // interval; the snapshot endpoint is cheap and the throttled backend
    // write means there's nothing fresher to wait for.
    void poll()
    const id = window.setInterval(() => void poll(), POLL_INTERVAL_MS)
    return () => {
      cancelled = true
      window.clearInterval(id)
    }
  }, [isActive, sessionId, skipPolling])
}
